import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Captura un elemento como imagen usando html2canvas, retornando la imagen y sus dimensiones.
 * @param {string} elementId - ID del elemento a capturar.
 * @returns {Promise<{dataUrl: string, width: number, height: number}|null>} Imagen y dimensiones o null si falla.
 */
const captureElementAsImage = async (elementId) => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      console.warn(`Elemento no encontrado: ${elementId}`);
      return null;
    }

    const canvas = await html2canvas(element, {
      scale: 3, // Aumentado para leyendas más grandes
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    return {
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    };
  } catch (error) {
    console.error(`Error capturando ${elementId}:`, error);
    return null;
  }
};

/**
 * Dibuja una tabla estilizada en el PDF.
 * @param {jsPDF} doc - Instancia de jsPDF.
 * @param {Object} section - Configuracion de la seccion.
 * @param {number} startX - Coordenada inicial X.
 * @param {number} startY - Coordenada inicial Y.
 * @param {number} tableWidth - Ancho de tabla.
 * @returns {number} Altura usada por la tabla.
 */
const drawPdfTable = (doc, section, startX, startY, tableWidth) => {
  const rowHeight = 8;
  const columns = section.columns;
  const columnWidths = Array.isArray(section.columnWidths) &&
    section.columnWidths.length === columns.length
    ? section.columnWidths
    : columns.map(() => 1 / columns.length);
  let cursorY = startY;

  doc.setFillColor(204, 239, 208);
  doc.rect(startX, cursorY, tableWidth, rowHeight, 'F');
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');

  columns.forEach((column, index) => {
    const offsetX = columnWidths
      .slice(0, index)
      .reduce((acc, width) => acc + width * tableWidth, 0);
    const textX = startX + offsetX + 2;
    doc.text(column.label, textX, cursorY + 5.5);
  });

  cursorY += rowHeight;
  doc.setFont('helvetica', 'normal');

  section.rows.forEach((row) => {
    doc.setDrawColor(210);
    doc.rect(startX, cursorY, tableWidth, rowHeight);

    columns.forEach((column, index) => {
      const value = row?.[column.key] != null ? String(row[column.key]) : '-';
      const offsetX = columnWidths
        .slice(0, index)
        .reduce((acc, width) => acc + width * tableWidth, 0);
      const textX = startX + offsetX + 2;
      doc.text(value, textX, cursorY + 5.5);
    });

    cursorY += rowHeight;
  });

  doc.setDrawColor(210);
  doc.rect(startX, cursorY, tableWidth, rowHeight);
  columns.forEach((column, index) => {
    let value = '';
    if (section.totalRowValues && Object.prototype.hasOwnProperty.call(section.totalRowValues, column.key)) {
      value = String(section.totalRowValues[column.key]);
    } else {
      if (index === 0) value = section.totalLabel;
      if (index === columns.length - 1) value = String(section.totalCount);
    }
    const offsetX = columnWidths
      .slice(0, index)
      .reduce((acc, width) => acc + width * tableWidth, 0);
    const textX = startX + offsetX + 2;
    doc.setFont('helvetica', 'bold');
    doc.text(value, textX, cursorY + 5.5);
  });
  doc.setFont('helvetica', 'normal');

  return cursorY + rowHeight - startY;
};

/**
 * Calcula el porcentaje de un valor sobre el total.
 * @param {number} value - Valor base.
 * @param {number} total - Total de referencia.
 * @returns {string} Porcentaje formateado.
 */
const formatPercentage = (value, total) => {
  if (!total) return '0%';
  const percent = (Number(value || 0) / total) * 100;
  return `${percent.toFixed(2)}%`;
};

/**
 * Agrega porcentaje calculado a cada fila segun el total.
 * @param {Array<Object>} rows - Filas originales.
 * @param {number} total - Total de referencia.
 * @param {string} countKey - Clave numérica a usar.
 * @returns {Array<Object>} Filas con porcentaje.
 */
const addPercentageToRows = (rows, total, countKey = 'count') => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    ...row,
    percentage: formatPercentage(row?.[countKey], total),
  }));
};

/**
 * Genera la estructura de secciones para el PDF de detalle de contratos.
 * @param {Object} reportResults - Resultados del reporte.
 * @param {Object|null} reportHeader - Header del reporte.
 * @returns {Array<Object>} Secciones con tabla y grafico.
 */
const buildContractDetailsPdfSections = (reportResults, reportHeader) => {
  const totalContracts =
    reportHeader?.total_contracts != null ? reportHeader.total_contracts : 0;
  const updatedAt = reportHeader?.updated_at_date || 'Sin fecha';

  return [
    {
      title: 'Contratos por tipo de cuota',
      subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
      columns: [
        { key: 'label', label: 'Tipo cuota', align: 'left' },
        { key: 'count', label: 'Cantidad', align: 'center' },
        { key: 'percentage', label: 'Porcentaje', align: 'center' },
      ],
      columnWidths: [0.58, 0.2, 0.22],
      rows: addPercentageToRows(reportResults.fee_type || [], totalContracts),
      chartId: 'gerenciales-contract-details-fee-type',
      totalLabel: 'Total contratos',
      totalCount: totalContracts,
      totalRowValues: {
        label: 'Total contratos',
        count: totalContracts,
        percentage: '100%',
      },
    },
    {
      title: 'Contratos por tipo de posesion',
      subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
      columns: [
        { key: 'label', label: 'Tipo posesion', align: 'left' },
        { key: 'count', label: 'Cantidad', align: 'center' },
        { key: 'percentage', label: 'Porcentaje', align: 'center' },
      ],
      columnWidths: [0.58, 0.2, 0.22],
      rows: addPercentageToRows(reportResults.possession || [], totalContracts),
      chartId: 'gerenciales-contract-details-possession',
      totalLabel: 'Total contratos',
      totalCount: totalContracts,
      totalRowValues: {
        label: 'Total contratos',
        count: totalContracts,
        percentage: '100%',
      },
    },
    {
      title: 'Contratos por tipo de cliente',
      subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
      columns: [
        { key: 'label', label: 'Tipo cliente', align: 'left' },
        { key: 'count', label: 'Cantidad', align: 'center' },
        { key: 'percentage', label: 'Porcentaje', align: 'center' },
      ],
      columnWidths: [0.58, 0.2, 0.22],
      rows: addPercentageToRows(reportResults.client_type || [], totalContracts),
      chartId: 'gerenciales-contract-details-client-type',
      totalLabel: 'Total contratos',
      totalCount: totalContracts,
      totalRowValues: {
        label: 'Total contratos',
        count: totalContracts,
        percentage: '100%',
      },
    },
    {
      title: 'Contratos por tipo de cuota y posesion',
      subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
      columns: [
        { key: 'fee_type', label: 'Tipo cuota', align: 'left' },
        { key: 'possession', label: 'Tipo posesion', align: 'left' },
        { key: 'count', label: 'Cantidad', align: 'center' },
        { key: 'percentage', label: 'Porcentaje', align: 'center' },
      ],
      columnWidths: [0.34, 0.30, 0.16, 0.20],
      rows: addPercentageToRows(reportResults.fee_type_possession || [], totalContracts),
      chartId: 'gerenciales-contract-details-fee-type-possession',
      totalLabel: 'Total contratos',
      totalCount: totalContracts,
      totalRowValues: {
        fee_type: 'Total contratos',
        count: totalContracts,
        percentage: '100%',
      },
    },
    {
      title: 'Contratos por tipo de cliente y posesion',
      subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
      columns: [
        { key: 'client_type', label: 'Tipo cliente', align: 'left' },
        { key: 'possession', label: 'Tipo posesion', align: 'left' },
        { key: 'count', label: 'Cantidad', align: 'center' },
        { key: 'percentage', label: 'Porcentaje', align: 'center' },
      ],
      columnWidths: [0.34, 0.30, 0.16, 0.20],
      rows: addPercentageToRows(reportResults.client_type_possession || [], totalContracts),
      chartId: 'gerenciales-contract-details-client-type-possession',
      totalLabel: 'Total contratos',
      totalCount: totalContracts,
      totalRowValues: {
        client_type: 'Total contratos',
        count: totalContracts,
        percentage: '100%',
      },
    },
  ];
};

/**
 * Genera la estructura de seccion para el PDF de informe de pagos.
 * @param {Array<Object>} reportResults - Resultados del reporte.
 * @param {Object|null} reportHeader - Header del reporte.
 * @returns {Object} Seccion con tabla y grafico.
 */
const buildPaymentsReportSection = (reportResults, reportHeader) => {
  const totalPayments =
    reportHeader?.total_payments != null ? reportHeader.total_payments : 0;
  const updatedAt = reportHeader?.updated_at_date || 'Sin fecha';
  const dateFilterText = reportHeader?.date_filter_text || 'Sin filtros de fecha';
  const tableRows = Array.isArray(reportResults)
    ? reportResults
    : Array.isArray(reportResults?.table)
      ? reportResults.table
      : [];

  return {
    title: 'Pagos por entidad legal',
    subtitle: `Actualizado: ${updatedAt} • ${dateFilterText} • Total pagos: ${totalPayments}`,
    columns: [
      { key: 'legal_entity_name', label: 'Entidad legal', align: 'left' },
      { key: 'count', label: 'Pagos', align: 'center' },
      { key: 'percentage', label: 'Porcentaje', align: 'center' },
    ],
    columnWidths: [0.58, 0.2, 0.22],
    rows: addPercentageToRows(tableRows, totalPayments),
    chartId: 'gerenciales-payments-report-legal-entity',
    totalLabel: 'Total pagos',
    totalCount: totalPayments,
    totalRowValues: {
      legal_entity_name: 'Total pagos',
      count: totalPayments,
      percentage: '100%',
    },
  };
};

/**
 * Genera PDF para el reporte de detalles de contratos.
 * @param {Object} results - Resultados del reporte.
 * @param {Object|null} header - Header del reporte.
 * @returns {Promise<void>}
 */
export const generateContractDetailsPdf = async (results, header) => {
  try {
    // Delay para asegurar que los gráficos estén renderizados
    await new Promise((resolve) => setTimeout(resolve, 500));

    const sections = buildContractDetailsPdfSections(results, header);
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;

    for (let index = 0; index < sections.length; index += 1) {
      if (index > 0) doc.addPage();
      const section = sections[index];

      // Header de la sección
      doc.setFillColor(59, 63, 70);
      doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text(section.title, margin + 3, margin + 7);

      // Subtitle
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(section.subtitle, margin + 3, margin + 16);

      // TABLA ARRIBA
      const tableX = margin;
      const tableY = margin + 20;
      const tableWidth = pageWidth * 0.4;
      const tableHeight = drawPdfTable(doc, section, tableX, tableY, tableWidth);

      // GRÁFICO ABAJO - USA TODO EL ANCHO
      const chartY = tableY + tableHeight + 10; // Debajo de la tabla
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - chartY - margin;

      console.log(`Capturando gráfico: ${section.chartId}`);
      const chartData = await captureElementAsImage(section.chartId);

      if (chartData) {
        // Calcular aspecto ratio original
        const originalAspectRatio = chartData.width / chartData.height;

        // Calcular dimensiones manteniendo proporción
        let chartWidth, chartHeight;

        // USA TODO EL ESPACIO DISPONIBLE
        const maxWidth = availableWidth;
        const maxHeight = availableHeight;

        // Ajustar manteniendo proporción
        if (maxWidth / originalAspectRatio <= maxHeight) {
          // Limitado por ancho
          chartWidth = maxWidth;
          chartHeight = maxWidth / originalAspectRatio;
        } else {
          // Limitado por altura
          chartHeight = maxHeight;
          chartWidth = maxHeight * originalAspectRatio;
        }

        // Centrar horizontalmente
        const chartX = margin + (availableWidth - chartWidth) / 2;

        doc.addImage(chartData.dataUrl, 'PNG', chartX, chartY, chartWidth, chartHeight);
        console.log(`Gráfico ${section.chartId} agregado: ${chartWidth.toFixed(1)}x${chartHeight.toFixed(1)}`);
      } else {
        console.warn(`No se pudo capturar el gráfico: ${section.chartId}`);
        // Dibujar un rectángulo con mensaje de error
        const errorSize = Math.min(availableWidth, availableHeight);
        const errorX = margin + (availableWidth - errorSize) / 2;
        const errorY = chartY + (availableHeight - errorSize / 2) / 2;

        doc.setDrawColor(200);
        doc.setFillColor(245, 245, 245);
        doc.rect(errorX, errorY, errorSize, errorSize / 2, 'FD');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Error al cargar gráfico', errorX + errorSize / 2, errorY + errorSize / 4, {
          align: 'center',
        });
      }
    }

    doc.save('Estadisticas_Gerenciales_Contrato.pdf');
    console.log('PDF generado exitosamente');
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  }
};

/**
 * Genera PDF para el reporte de pagos.
 * @param {Array<Object>} results - Resultados del reporte.
 * @param {Object|null} header - Header del reporte.
 * @returns {Promise<void>}
 */
export const generatePaymentsReportPdf = async (results, header) => {
  try {
    // Delay para asegurar que los gráficos estén renderizados
    await new Promise((resolve) => setTimeout(resolve, 500));

    const section = buildPaymentsReportSection(results, header);
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;

    doc.setFillColor(59, 63, 70);
    doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(section.title, margin + 3, margin + 7);

    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(section.subtitle, margin + 3, margin + 16);

    const tableX = margin;
    const tableY = margin + 20;
    const tableWidth = pageWidth * 0.4;
    const tableHeight = drawPdfTable(doc, section, tableX, tableY, tableWidth);

    const chartY = tableY + tableHeight + 10;
    const availableWidth = pageWidth - margin * 2;
    const availableHeight = pageHeight - chartY - margin;

    const chartData = await captureElementAsImage(section.chartId);

    if (chartData) {
      const originalAspectRatio = chartData.width / chartData.height;
      const maxWidth = availableWidth;
      const maxHeight = availableHeight;

      let chartWidth;
      let chartHeight;

      if (maxWidth / originalAspectRatio <= maxHeight) {
        chartWidth = maxWidth;
        chartHeight = maxWidth / originalAspectRatio;
      } else {
        chartHeight = maxHeight;
        chartWidth = maxHeight * originalAspectRatio;
      }

      const chartX = margin + (availableWidth - chartWidth) / 2;
      doc.addImage(chartData.dataUrl, 'PNG', chartX, chartY, chartWidth, chartHeight);
    } else {
      const errorSize = Math.min(availableWidth, availableHeight);
      const errorX = margin + (availableWidth - errorSize) / 2;
      const errorY = chartY + (availableHeight - errorSize / 2) / 2;

      doc.setDrawColor(200);
      doc.setFillColor(245, 245, 245);
      doc.rect(errorX, errorY, errorSize, errorSize / 2, 'FD');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Error al cargar gráfico', errorX + errorSize / 2, errorY + errorSize / 4, {
        align: 'center',
      });
    }

    doc.save('Estadisticas_Gerenciales_Pagos.pdf');
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  }
};

/**
 * Genera PDF para el reporte de mora por estado (gráficos).
 * @param {Object} results - Resultados del reporte.
 * @param {Object|null} header - Header del reporte.
 * @returns {Promise<void>}
 */
export const generateDebtStatusChartsPdf = async (results, header) => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const updatedAt = header?.updated_at_date || 'Sin fecha';
    const totalContracts =
      header?.total_contracts != null ? header.total_contracts : 0;

    const charts = [
      {
        title: 'Morosos por estado (Global)',
        chartId: 'gerenciales-debt-status-global',
      },
      {
        title: 'Morosos por estado (Poseedor vs No poseedor)',
        chartId: 'gerenciales-debt-status-grouped',
      },
      {
        title: 'Importe total de mora por estado',
        chartId: 'gerenciales-debt-status-amounts',
      },
      {
        title: 'Estado de deuda por Forma de pago',
        chartId: 'gerenciales-debt-status-payment-method',
      },
    ];

    for (let index = 0; index < charts.length; index += 1) {
      if (index > 0) doc.addPage();
      const chart = charts[index];

      doc.setFillColor(59, 63, 70);
      doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text(chart.title, margin + 3, margin + 7);

      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        margin + 3,
        margin + 16
      );

      const chartY = margin + 22;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - chartY - margin;

      const chartData = await captureElementAsImage(chart.chartId);
      if (chartData) {
        const originalAspectRatio = chartData.width / chartData.height;
        const maxWidth = availableWidth;
        const maxHeight = availableHeight;

        let chartWidth;
        let chartHeight;

        if (maxWidth / originalAspectRatio <= maxHeight) {
          chartWidth = maxWidth;
          chartHeight = maxWidth / originalAspectRatio;
        } else {
          chartHeight = maxHeight;
          chartWidth = maxHeight * originalAspectRatio;
        }

        const chartX = margin + (availableWidth - chartWidth) / 2;
        doc.addImage(chartData.dataUrl, 'PNG', chartX, chartY, chartWidth, chartHeight);
      } else {
        const errorSize = Math.min(availableWidth, availableHeight);
        const errorX = margin + (availableWidth - errorSize) / 2;
        const errorY = chartY + (availableHeight - errorSize / 2) / 2;

        doc.setDrawColor(200);
        doc.setFillColor(245, 245, 245);
        doc.rect(errorX, errorY, errorSize, errorSize / 2, 'FD');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Error al cargar gráfico', errorX + errorSize / 2, errorY + errorSize / 4, {
          align: 'center',
        });
      }
    }

    doc.save('Estadisticas_Gerenciales_Mora_Estado.pdf');
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  }
};

/**
 * Genera PDF para el reporte de suscriptores en app celular (gráficos).
 * @param {Object} results - Resultados del reporte.
 * @param {Object|null} header - Header del reporte.
 * @returns {Promise<void>}
 */
export const generateAppSubscribersChartsPdf = async (results, header) => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const updatedAt = header?.updated_at_date || 'Sin fecha';
    const totalSubscribers =
      header?.total_subscribers != null ? header.total_subscribers : 0;

    const charts = [
      {
        title: 'Uso de la app vs No uso',
        chartId: 'gerenciales-app-subscribers-usage',
      },
      {
        title: 'Uso de la app en hombres por rango etario',
        chartId: 'gerenciales-app-subscribers-men-age',
      },
      {
        title: 'Uso de la app en mujeres por rango etario',
        chartId: 'gerenciales-app-subscribers-women-age',
      },
      {
        title: 'Uso de la app en general por rango etario',
        chartId: 'gerenciales-app-subscribers-general-age',
      },
    ];

    for (let index = 0; index < charts.length; index += 1) {
      if (index > 0) doc.addPage();
      const chart = charts[index];

      doc.setFillColor(59, 63, 70);
      doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text(chart.title, margin + 3, margin + 7);

      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Actualizado: ${updatedAt} • Total suscriptores: ${totalSubscribers}`,
        margin + 3,
        margin + 16
      );

      const chartY = margin + 22;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - chartY - margin;

      const chartData = await captureElementAsImage(chart.chartId);
      if (chartData) {
        const originalAspectRatio = chartData.width / chartData.height;
        const maxWidth = availableWidth;
        const maxHeight = availableHeight;

        let chartWidth;
        let chartHeight;

        if (maxWidth / originalAspectRatio <= maxHeight) {
          chartWidth = maxWidth;
          chartHeight = maxWidth / originalAspectRatio;
        } else {
          chartHeight = maxHeight;
          chartWidth = maxHeight * originalAspectRatio;
        }

        const chartX = margin + (availableWidth - chartWidth) / 2;
        doc.addImage(chartData.dataUrl, 'PNG', chartX, chartY, chartWidth, chartHeight);
      } else {
        const errorSize = Math.min(availableWidth, availableHeight);
        const errorX = margin + (availableWidth - errorSize) / 2;
        const errorY = chartY + (availableHeight - errorSize / 2) / 2;

        doc.setDrawColor(200);
        doc.setFillColor(245, 245, 245);
        doc.rect(errorX, errorY, errorSize, errorSize / 2, 'FD');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Error al cargar gráfico', errorX + errorSize / 2, errorY + errorSize / 4, {
          align: 'center',
        });
      }
    }

    doc.save('Suscriptores_App_Celular.pdf');
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  }
};

/**
 * Genera PDF para el reporte de motivos de baja (gráficos).
 * @param {Object} results - Resultados del reporte.
 * @param {Object|null} header - Header del reporte.
 * @returns {Promise<void>}
 */
export const generateArchivedContractsChartsPdf = async (results, header) => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const updatedAt = header?.updated_at_date || 'Sin fecha';
    const totalContracts =
      header?.total_contracts != null ? header.total_contracts : 0;

    const charts = [
      {
        title: 'Contratos dados de baja por motivo',
        chartId: 'gerenciales-archived-by-reason',
      },
      {
        title: 'Contratos dados de baja por distrito',
        chartId: 'gerenciales-archived-by-district',
      },
      {
        title: 'Contratos dados de baja por año',
        chartId: 'gerenciales-archived-by-year',
      },
    ];

    for (let index = 0; index < charts.length; index += 1) {
      if (index > 0) doc.addPage();
      const chart = charts[index];

      doc.setFillColor(59, 63, 70);
      doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text(chart.title, margin + 3, margin + 7);

      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        margin + 3,
        margin + 16
      );

      const chartY = margin + 22;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - chartY - margin;

      const chartData = await captureElementAsImage(chart.chartId);
      if (chartData) {
        const originalAspectRatio = chartData.width / chartData.height;
        const maxWidth = availableWidth;
        const maxHeight = availableHeight;

        let chartWidth;
        let chartHeight;

        if (maxWidth / originalAspectRatio <= maxHeight) {
          chartWidth = maxWidth;
          chartHeight = maxWidth / originalAspectRatio;
        } else {
          chartHeight = maxHeight;
          chartWidth = maxHeight * originalAspectRatio;
        }

        const chartX = margin + (availableWidth - chartWidth) / 2;
        doc.addImage(chartData.dataUrl, 'PNG', chartX, chartY, chartWidth, chartHeight);
      } else {
        const errorSize = Math.min(availableWidth, availableHeight);
        const errorX = margin + (availableWidth - errorSize) / 2;
        const errorY = chartY + (availableHeight - errorSize / 2) / 2;

        doc.setDrawColor(200);
        doc.setFillColor(245, 245, 245);
        doc.rect(errorX, errorY, errorSize, errorSize / 2, 'FD');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Error al cargar gráfico', errorX + errorSize / 2, errorY + errorSize / 4, {
          align: 'center',
        });
      }
    }

    doc.save('Motivos_Baja_Contratos.pdf');
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  }
};
